import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Search as SearchIcon, Users, MousePointerClick, XCircle, BarChart2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

// Dummy brand ID for demo data
const DUMMY_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

// Build dummy search terms data
const buildDummySearchData = () => {
  const searchTerms = [
    { term: 'elbise', baseQueries: 4500 },
    { term: 'ayakkabı', baseQueries: 3800 },
    { term: 'çanta', baseQueries: 3200 },
    { term: 'mont', baseQueries: 2900 },
    { term: 'pantolon', baseQueries: 2600 },
    { term: 'gömlek', baseQueries: 2400 },
    { term: 'ceket', baseQueries: 2100 },
    { term: 'kazak', baseQueries: 1900 },
    { term: 'bluz', baseQueries: 1700 },
    { term: 'etek', baseQueries: 1500 },
    { term: 'tişört', baseQueries: 1400 },
    { term: 'jean', baseQueries: 1300 },
    { term: 'triko', baseQueries: 1200 },
    { term: 'sweatshirt', baseQueries: 1100 },
    { term: 'hırka', baseQueries: 1000 },
    { term: 'kaban', baseQueries: 950 },
    { term: 'yelek', baseQueries: 850 },
    { term: 'şort', baseQueries: 780 },
    { term: 'eşofman', baseQueries: 720 },
    { term: 'pijama', baseQueries: 650 },
    { term: 'mayo', baseQueries: 580 },
    { term: 'aksesuar', baseQueries: 520 },
    { term: 'kemer', baseQueries: 480 },
    { term: 'şapka', baseQueries: 420 },
    { term: 'çorap', baseQueries: 380 }
  ];
  
  const today = new Date();
  const monthly = [];
  const termsByMonth = {};
  const topTermsMap = new Map();
  
  // Generate 12 months of data
  for (let i = 11; i >= 0; i--) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const monthKey = monthDate.toISOString().slice(0, 10);
    
    // Seasonal variance (higher in winter months for fashion)
    const month = monthDate.getMonth();
    const seasonalFactor = [1.1, 1.0, 0.9, 0.85, 0.8, 0.75, 0.7, 0.8, 0.95, 1.1, 1.3, 1.4][month];
    
    let totalQueries = 0;
    const monthTerms = [];
    
    searchTerms.forEach(t => {
      const variance = 0.7 + Math.random() * 0.6;
      const queries = Math.round(t.baseQueries * seasonalFactor * variance);
      const resultsCount = Math.round(queries * (0.85 + Math.random() * 0.1));
      
      totalQueries += queries;
      monthTerms.push({
        term: t.term,
        queries,
        resultsCount
      });
      
      // Accumulate for top terms
      const existing = topTermsMap.get(t.term) || { term: t.term, queries: 0 };
      existing.queries += queries;
      topTermsMap.set(t.term, existing);
    });
    
    // Sort by queries desc
    monthTerms.sort((a, b) => b.queries - a.queries);
    
    monthly.push({
      month: monthKey,
      queries: totalQueries,
      uniqueUsers: Math.round(totalQueries * 0.65),
      unique_terms: searchTerms.length,
      zeroResultPct: (2 + Math.random() * 3).toFixed(1),
      resultCtr: (35 + Math.random() * 15).toFixed(1)
    });
    
    termsByMonth[monthKey] = monthTerms;
  }
  
  // Build top terms sorted
  const topTerms = Array.from(topTermsMap.values())
    .sort((a, b) => b.queries - a.queries)
    .slice(0, 10);
  
  return {
    monthly,
    termsByMonth,
    topTerms,
    uniqueTermCount: searchTerms.length
  };
};

export default function InternalSearchTerms() {
  const [baseDataAll, setBaseDataAll] = useState(null); // unfiltered (for trend/breakdown)
  const [filteredData, setFilteredData] = useState(null); // respects dateRange (for KPI + Top10 + MonthlyReport)
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [year] = useState(new Date().getFullYear());
  const months = useMemo(() => (
    [
      'January','February','March','April','May','June','July','August','September','October','November','December'
    ]
  ), []);

  // Deterministic pseudo-random generator for stable dummy data
  const seedNumber = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i);
    return Math.abs(h);
  };
  const seeded = (key, min, max) => {
    const s = seedNumber(key);
    const r = Math.abs(Math.sin(s) * 10000) % 1;
    return Math.round(min + r * (max - min));
  };

  const searchTerms = [
    'smoking', 'takım elbise', 'gömlek', 'kravat', 'polo yaka', 'keten', 'damatlık', 'ceket', 'ayakkabı', 'aksesuar',
    'iphone', 'macbook', 'airpods', 'watch', 'ipad'
  ];

  // Fetch live data
  // Fetch ALL (once)
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        
        // Use dummy data for special brand
        if (brandId === DUMMY_BRAND_ID) {
          const dummyData = buildDummySearchData();
          setBaseDataAll(dummyData);
          return;
        }
        
        const response = await fetch(`https://api.glowytics.com/api/google-ads/ga-site-search?dateRange=all`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId || ''
          },
          credentials: 'include'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        // Normalize termsByMonth keys to 'YYYY-MM-DD'
        let normalizedTermsByMonth = {};
        if (data && data.termsByMonth) {
          Object.keys(data.termsByMonth).forEach((k) => {
            const key = (() => {
              try { return new Date(k).toISOString().slice(0,10); } catch { return String(k); }
            })();
            normalizedTermsByMonth[key] = data.termsByMonth[k];
          });
        }
        setBaseDataAll({ ...data, termsByMonth: normalizedTermsByMonth });
      } catch (e) {
        console.error('InternalSearchTerms fetch error:', e.message);
      }
    };
    fetchAll();
  }, []);

  // Fetch FILTERED whenever dateRange changes
  useEffect(() => {
    const fetchFiltered = async () => {
      try {
        setLoading(true);
        const brandId = localStorage.getItem('currentBrandId');
        
        // Use dummy data for special brand
        if (brandId === DUMMY_BRAND_ID) {
          const dummyData = buildDummySearchData();
          
          // Filter based on dateRange
          let filteredMonthly = dummyData.monthly;
          const today = new Date();
          
          if (dateRange === 'yesterday' || dateRange === 'last7' || dateRange === 'last30') {
            // For short ranges, just use the most recent month's data
            filteredMonthly = dummyData.monthly.slice(-1);
          } else if (dateRange === 'thisMonth') {
            filteredMonthly = dummyData.monthly.slice(-1);
          } else if (dateRange === 'lastMonth') {
            filteredMonthly = dummyData.monthly.slice(-2, -1);
          }
          // 'all' keeps all data
          
          // Recalculate top terms for filtered period
          const filteredTopTermsMap = new Map();
          const filteredTermsByMonth = {};
          
          filteredMonthly.forEach(m => {
            const monthTerms = dummyData.termsByMonth[m.month] || [];
            filteredTermsByMonth[m.month] = monthTerms;
            monthTerms.forEach(t => {
              const existing = filteredTopTermsMap.get(t.term) || { term: t.term, queries: 0 };
              existing.queries += t.queries;
              filteredTopTermsMap.set(t.term, existing);
            });
          });
          
          const filteredTopTerms = Array.from(filteredTopTermsMap.values())
            .sort((a, b) => b.queries - a.queries)
            .slice(0, 10);
          
          setFilteredData({
            monthly: filteredMonthly,
            termsByMonth: filteredTermsByMonth,
            topTerms: filteredTopTerms,
            uniqueTermCount: dummyData.uniqueTermCount
          });
          setLoading(false);
          return;
        }
        
        const response = await fetch(`https://api.glowytics.com/api/google-ads/ga-site-search?dateRange=${dateRange}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId || ''
          },
          credentials: 'include'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        let normalizedTermsByMonth = {};
        if (data && data.termsByMonth) {
          Object.keys(data.termsByMonth).forEach((k) => {
            const key = (() => {
              try { return new Date(k).toISOString().slice(0,10); } catch { return String(k); }
            })();
            normalizedTermsByMonth[key] = data.termsByMonth[k];
          });
        }
        setFilteredData({ ...data, termsByMonth: normalizedTermsByMonth });
      } catch (e) {
        console.error('InternalSearchTerms filtered fetch error:', e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchFiltered();
  }, [dateRange]);

  // Build dataset (API only; no dummy fallback)
  const monthData = useMemo(() => {
    const apiData = baseDataAll;
    if (apiData && Array.isArray(apiData.monthly) && apiData.monthly.length) {
      // Sort by month asc (no limit)
      const sorted = [...apiData.monthly].sort((a, b) => new Date(a.month) - new Date(b.month));
      return sorted.map((row) => {
        const key = new Date(row.month).toISOString().slice(0, 10);
        const label = new Date(row.month).toLocaleDateString('en-US', { month: 'long' });
        const termList = (apiData.termsByMonth && apiData.termsByMonth[key]) ? apiData.termsByMonth[key] : [];
        const clicks = termList.reduce((s, r) => s + (Number(r.resultsCount || 0)), 0);
        const results = termList.map(t => ({ term: t.term, queries: Number(t.queries || 0), clicks: Number(t.resultsCount || 0), zeroRate: 0 }));
        return {
          month: label,
          totalQueries: Number(row.queries || 0),
          uniqueUsers: Number(row.uniqueUsers || 0),
          zeroResult: Number(row.zeroResultPct || 0),
          clickThrough: Number(row.resultCtr || 0),
          uniqueTerms: (row.unique_terms === undefined || row.unique_terms === null)
            ? Number(row.queries || 0)
            : Number(row.unique_terms || 0),
          clicks,
          results
        };
      });
    }
    return [];
  }, [baseDataAll, months, year]);

  // Filtered dataset for KPI + Top10 + Monthly Report
  const filteredMonthData = useMemo(() => {
    const apiData = filteredData;
    if (apiData && Array.isArray(apiData.monthly) && apiData.monthly.length) {
      const sorted = [...apiData.monthly].sort((a, b) => new Date(a.month) - new Date(b.month));
      return sorted.map((row) => {
        const key = new Date(row.month).toISOString().slice(0, 10);
        const label = new Date(row.month).toLocaleDateString('en-US', { month: 'long' });
        const termList = (apiData.termsByMonth && apiData.termsByMonth[key]) ? apiData.termsByMonth[key] : [];
        const clicks = termList.reduce((s, r) => s + (Number(r.resultsCount || 0)), 0);
        const results = termList.map(t => ({ term: t.term, queries: Number(t.queries || 0), clicks: Number(t.resultsCount || 0), zeroRate: 0 }));
        return {
          month: label,
          totalQueries: Number(row.queries || 0),
          uniqueTerms: (row.unique_terms === undefined || row.unique_terms === null)
            ? Number(row.queries || 0)
            : Number(row.unique_terms || 0),
          clicks,
          results
        };
      });
    }
    return [];
  }, [filteredData]);

  const totals = useMemo(() => {
    return filteredMonthData.reduce((acc, m) => {
      acc.totalQueries += m.totalQueries;
      acc.clicks += m.clicks;
      return acc;
    }, { totalQueries: 0, clicks: 0 });
  }, [filteredMonthData]);

  const uniqueTerms = useMemo(() => {
    if (filteredData?.uniqueTermCount != null) return filteredData.uniqueTermCount;
    const set = new Set();
    filteredMonthData.forEach(m => (m.results || []).forEach(r => { if (r.term) set.add(r.term); }));
    return set.size;
  }, [filteredData, filteredMonthData]);

  // Simple area chart path generator
  const buildAreaPath = (values, width = 700, height = 180, pad = 12) => {
    if (!values.length) return '';
    const maxVal = Math.max(...values) || 1;
    const stepX = (width - pad * 2) / (values.length - 1 || 1);
    const pts = values.map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (1 - v / maxVal) * (height - pad * 2);
      return [x, y];
    });
    let d = `M ${pad} ${height - pad} L ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 1; i < pts.length; i++) d += ` L ${pts[i][0]} ${pts[i][1]}`;
    d += ` L ${width - pad} ${height - pad} Z`;
    return d;
  };

  const latest = monthData[monthData.length - 1] || { results: [], month: '' };

  // Aggregated per-term across the year
  const aggregated = useMemo(() => {
    const map = new Map();
    monthData.forEach((m) => {
      m.results.forEach((r) => {
        const k = r.term;
        if (!map.has(k)) map.set(k, { term: k, queries: 0, clicks: 0 });
        const v = map.get(k);
        v.queries += r.queries;
        v.clicks += r.clicks;
      });
    });
    return Array.from(map.values()).sort((a,b) => b.queries - a.queries);
  }, [monthData]);

  const [exporting, setExporting] = useState(false);
  const handleExportExcel = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const response = await fetch('https://api.glowytics.com/api/google-ads/ga-site-search/raw-export', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Export failed');
      const json = await response.json();
      const rows = json.rows || [];
      if (!rows.length) { alert('No data to export'); return; }
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 10 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Raw Search Data');
      XLSX.writeFile(wb, 'Internal_Search_Terms_Raw.xlsx');
    } catch (err) { console.error('Export error:', err); }
    finally { setExporting(false); }
  }, [exporting]);

  const tableTerms = aggregated.map(t => t.term);
  const [termQuery, setTermQuery] = useState('');
  const filteredTableTerms = useMemo(() => {
    const q = termQuery.trim().toLowerCase();
    if (!q) return tableTerms;
    return tableTerms.filter(t => t?.toLowerCase().includes(q));
  }, [tableTerms, termQuery]);

  return (
    <div className="p-6">
      {/* Header + KPI row (GA4 Overview style) */}
      <div className="bg-white rounded-3xl shadow-sm border overflow-hidden mb-6">
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Google-Analytics-Logo.png" alt="Google Analytics" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">On‑site search</h1>
              <p className="text-gray-500 text-sm">On‑site search analytics • {year}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {[
                {key: 'yesterday', label: 'Yesterday'},
                {key: 'last7', label: '7 Days'},
                {key: 'last30', label: '30 Days'},
                {key: 'thisMonth', label: 'This Month'},
                {key: 'lastMonth', label: 'Last Month'},
                {key: 'all', label: 'All Time'}
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setDateRange(opt.key)}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${dateRange===opt.key ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-800'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm transition-colors"
            >
              <Download size={14} />
              {exporting ? 'Exporting...' : 'Raw Excel'}
            </button>
          </div>
        </div>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 p-4">
          <div className="rounded-2xl p-4 bg-blue-600 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-sm opacity-90">Search Terms</div>
              <SearchIcon className="w-4 h-4" />
            </div>
            <div className="text-2xl font-bold mt-1">{uniqueTerms.toLocaleString('tr-TR')}</div>
          </div>
          <div className="rounded-2xl border p-4 bg-white">
            <div className="flex items-center justify-between text-gray-600 text-sm">
              <span>Search Count</span>
              <BarChart2 className="w-4 h-4" />
            </div>
            <div className="text-2xl font-bold mt-1">{totals.totalQueries.toLocaleString('tr-TR')}</div>
          </div>
        </div>
      </div>

      {/* Top 10 + Trend two-up */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="p-5 border-b">
            <h2 className="text-lg font-medium">Top 10 Terms <span className="text-gray-400 text-sm">({dateRange})</span></h2>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between text-xs text-gray-500 px-1 pb-2">
              <span>Term</span>
              <span>Search Count</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {(filteredData?.topTerms || []).slice(0,10).map((r, i) => (
                <li key={r.term} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 text-xs rounded-full bg-gray-100 text-gray-700 flex items-center justify-center">{i+1}</span>
                    <span className="text-sm font-medium text-gray-800">{r.term || '—'}</span>
                  </div>
                  <div className="text-sm text-gray-700">{r.queries.toLocaleString('tr-TR')}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="p-5 border-b">
            <h2 className="text-lg font-medium">Search Trend</h2>
            <p className="text-sm text-gray-500">Monthly total queries</p>
          </div>
          <div className="p-6">
            {(() => {
              const width = 1000; const height = 320; const pad = 28;
              const values = monthData.map(m => m.totalQueries);
              const labels = monthData.map(m => m.month.slice(0,3));
              const maxVal = Math.max(...values) || 1;
              const stepX = (width - pad * 2) / (values.length - 1 || 1);
              const points = values.map((v, i) => {
                const x = pad + i * stepX;
                const y = pad + (1 - v / maxVal) * (height - pad * 2);
                return { x, y, v, label: labels[i] };
              });
              const path = points.reduce((d, p, i) => d + (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), '');
              // 4 horizontal gridlines
              const grids = [0.25, 0.5, 0.75].map(r => pad + r * (height - pad * 2));
              return (
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-80">
                  <defs>
                    <linearGradient id="fillLine" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.04" />
                    </linearGradient>
                  </defs>
                  {/* Gridlines */}
                  {grids.map((gy, idx) => (
                    <line key={idx} x1={pad} x2={width - pad} y1={gy} y2={gy} stroke="#e5e7eb" strokeWidth="1" />
                  ))}
                  {/* Area under the line for subtle fill */}
                  <path d={`${path} L ${width - pad} ${height - pad} L ${pad} ${height - pad} Z`} fill="url(#fillLine)" />
                  {/* Line */}
                  <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2"/>
                  {/* Points and labels */}
                  {points.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r="3.5" fill="#3b82f6" />
                      <text x={p.x} y={height - pad + 14} fontSize="10" textAnchor="middle" fill="#6b7280">{p.label}</text>
                    </g>
                  ))}
                </svg>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Monthly Search Report (GA4-style toolbar) */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b bg-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/Google-Analytics-Logo.png" alt="GA4 Logo" className="w-6 h-6 object-contain" />
            <h3 className="text-lg font-semibold text-gray-900">Monthly Search Report</h3>
          </div>
          <div className="relative">
            <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="pl-8 pr-3 py-2 border rounded-lg text-sm w-64"
              placeholder="Search terms..."
              value={termQuery}
              onChange={(e) => setTermQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Term</th>
                {monthData.map((m) => (
                  <th key={m.month} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{m.month.slice(0,3)}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">TOTAL</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredTableTerms.map((term) => {
                const monthlyVals = monthData.map((data) => {
                  const found = (data?.results || []).find(r => r.term === term);
                  return found ? found.queries : 0;
                });
                const rowTotal = monthlyVals.reduce((a,b)=>a+b,0);
                return (
                  <tr key={term} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{term}</td>
                    {monthlyVals.map((v, i) => (
                      <td key={`${term}-${i}`} className="px-4 py-3 text-sm text-right">{v.toLocaleString('tr-TR')}</td>
                    ))}
                    <td className="px-4 py-3 text-sm text-right font-semibold">{rowTotal.toLocaleString('tr-TR')}</td>
                  </tr>
                );
              })}
              {/* TOTAL row */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-4 py-3 text-sm">TOTAL</td>
                {monthData.map((m, mi) => (
                  <td key={`tot-${mi}`} className="px-4 py-3 text-sm text-right">{(m.totalQueries || 0).toLocaleString('tr-TR')}</td>
                ))}
                <td className="px-4 py-3 text-sm text-right">{totals.totalQueries.toLocaleString('tr-TR')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-4">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium">Monthly Breakdown</h2>
          <p className="text-sm text-gray-500">Top 5 terms by month</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Terms</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Search Count</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Top Terms</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {[...monthData].slice().reverse().map((m) => {
                return (
                  <tr key={m.month} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-sm font-medium">{m.month}</td>
                    <td className="px-4 py-3 text-sm text-right">{(m.uniqueTerms || 0).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 text-sm text-right">{m.totalQueries.toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-2">
                        {m.results.slice(0,5).map(r => (
                          <span key={r.term} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                            <span className="font-medium">{r.term}</span>
                            <span className="text-gray-500">{r.queries.toLocaleString('tr-TR')}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


